import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card } from '@safetyhub/ui';
import {
  OrganizationMember,
  organizationsApi,
  approvalLineMetaApi,
  type UpdateSiteApprovalLineMetaDto,
} from '../../../../lib/organizations';
import { isWithdrawnMember } from '../../../../lib/memberStatus';
import { apiErrorMessage } from '../../../../lib/api';
import type { ApprovalLineMetaRow } from '@safetyhub/shared';
import { setReportApprovalLines, fetchComposedApprovalLinePreview, type ApprovalLinePreviewData } from '../../../../lib/reports';
// base row → 편집 행 변환의 정본(순수 모듈) — 위임 모달도 같은 함수를 쓴다(F-49).
import { buildItemsFromPreviewBaseRows } from './previewRows';
import type { ApprovalLineItem, ApprovalSignatureDisplay } from '@safetyhub/report-composer';
import {
  approvalPrefixViolationMessage,
  findApprovalPrefixViolation,
  findIdentityLostApprovalStep,
  identityLostApprovalStepMessage,
  isIdentityLostApprovalStep,
} from './approvalLineIdentity';
import {
  approvalBaseSignature,
  restorableAdditionalLines,
  type DelegationApprovalLine,
} from '../../../../utils/delegationDefaults';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../../../modalLayers';
import { useApprovalLineEdit, extractMemberDisplay } from './hooks/useApprovalLineEdit';
import { useApproverMemberForm } from './hooks/useApproverMemberForm';
import { ApprovalDisplayTable } from './components/ApprovalDisplayTable';
import { EditModal } from './components/EditModal';
import { ApproverContactFormModal } from '../../../approval/ApproverContactFormModal';
import { EditIcon } from './components/icons';

export type { ApprovalLineItem, ApprovalSignatureDisplay };

interface ResendApprovalInfo {
  canResend: boolean;
  currentApprover: {
    id: string;
    signerName?: string | null;
    signerTitle?: string | null;
    member?: { id: string; name: string } | null;
  } | null;
}

interface ApprovalLineSectionProps {
  companyId: string;
  /**
   * 보고서 id — report 모드에서 결재라인 저장(`setReportApprovalLines`)·되돌리기 대상이다.
   * standalone 모드(위임 모달)는 문서가 아직 없어 넘기지 않는다(구 DLR 보고서별 갈래는 삭제).
   */
  reportId?: string;
  siteId?: string;
  /**
   * 보고서의 템플릿 key. 현장 메타(`SiteApprovalLineMeta.templateKey`)의 축 값이다.
   * 이 값이 있을 때만 "이 현장 기본 결재선으로 저장"이 열린다(무엇에 저장할지 알 수 있어야 한다).
   * (결정 2026-07-24-composed-approval-site-axis)
   */
  templateKey?: string;
  /**
   * 외부에서 주입하는 결재라인 preview(템플릿 approvalLineMeta 기반 — 상세는 `fetchComposedApprovalLinePreview`,
   * 위임 모달은 같은 조회를 담당자 축까지 넣어 받는다). 이 값으로 초기 baseRows + 한도(maxAdditionalLines)를
   * 산정한다. 미제공이면 정책 없음으로 본다 — 별도 cascade 조회 갈래를 두지 않는다.
   */
  presetPreview?: ApprovalLinePreviewData;
  approvalLines: ApprovalLineItem[];
  onApprovalLinesChange: (lines: ApprovalLineItem[]) => void;
  editable?: boolean;
  isLoading?: boolean;
  approvalSignatures?: ApprovalSignatureDisplay[];
  authorSignatureData?: string;
  authorSignerName?: string;
  onAuthorSignatureClick?: () => void;
  /** 결재 단계 최대 추가 가능 수 (undefined = 무제한) */
  maxApprovers?: number;
  /** 첫 단계 고정 라벨. locked=true면 라벨 수정/삭제/이동 불가 */
  requiredFirstStep?: { label: string; locked: boolean };
  /** 결재 링크 재전송 정보 (비-DRAFT 상태에서 표시) */
  resendApprovalInfo?: ResendApprovalInfo;
  isResendingApproval?: boolean;
  onResendApproval?: () => void;
  /**
   * 'standalone': API 저장 없이 외부 value/onChange로 상태 관리(위임 모달). 초기값은 주입
   * `presetPreview`(baseRows)로만 만들고 회사 멤버 목록은 후보·복원 이름용으로 fetch한다.
   * 기본값 'report' — 보고서 결재라인 저장·되돌리기.
   */
  mode?: 'report' | 'standalone';
  /**
   * 카드 컨테이너("결재라인" 헤더 카드)와 결재란 표시 없이 버튼만 렌더한다.
   * 보고서 표시 단일화(Step D): 일반 보고서 상세는 양식 본문(mode="print")이 결재란을
   * 정적 표시하므로, 이 섹션은 결재라인 편집/링크재전송 버튼 → 모달만 양식 밖에 둔다.
   */
  chromeless?: boolean;
  /**
   * 외부에서 편집 모달을 강제로 열기 위한 트리거 카운터.
   * 값이 변경될 때마다 편집 모달을 엽니다(헤더 버튼 → 모달 연동).
   * 초기값(0)은 트리거 없음.
   */
  openEditTrigger?: number;
  /**
   * PENDING_APPROVAL **대기 칸 지정 모드**(결정 2026-08-27 park-and-resume C2) — 대기(parked)
   * 서명행의 `approvalLevel` 목록. 비어 있지 않으면 편집 모달이 이 칸들만 열고 나머지는 전부
   * 잠근다(단계 추가·삭제·순서·라벨·해제·채널 불가). 서버 게이트
   * (`setReportApprovalLines`의 PENDING 분기)와 같은 범위다 — 대기 칸의 담당자 지정 외 접촉은
   * 서버가 `APPROVAL_LINE_PARKED_ONLY_EDIT`로 거부한다.
   *
   * 판정 축은 **서명행**(`isApprovalSignatureParked`)이다 — 정의 컬럼에는 떠난 사람의 신원이
   * 남아 있어(자동 재개·park 모두 정의를 일부러 안 건드린다) 정의로는 대기를 판정할 수 없다.
   */
  parkedFillLevels?: number[] | null;
  /**
   * 이 섹션이 **다른 모달 안에서** 쓰일 때 true — 편집 모달을 sub 층(z-[90])으로 올린다.
   * 위임 모달(z-[80]) 안에서 결재라인 편집을 열면 종전에는 그 뒤로 숨었다(2026-08-11).
   */
  editModalIsSub?: boolean;
  /**
   * 위임 흐름 편집인가 — 체인 머리가 `delegatedTo`인 칸의 표시·조작을 가른다(EditModal props doc).
   * 호출부(위임 모달)만 이 값을 안다: 칸 성질만으로는 **위임하지 않은 보고서**의 같은 칸까지 잡는다.
   */
  delegationSlot?: 'pending' | 'assigned';
  /**
   * 지난 위임에서 저장해 둔 기본값(localStorage) — 고정 결재란 뒤에 추가 결재자를 복원한다.
   * 복원 가부는 이쪽에서 판정한다(정책 서명 대조·한도 클램프) — 호출부는 preview를 넘길 뿐
   * baseRows로 스스로 판정하지 않는다. cascade 초기화 경로에만 적용된다.
   */
  restoreFrom?: { additionalLines?: DelegationApprovalLine[]; baseSignature?: string; savedAt: number } | null;
  /**
   * 현장 기본(고정 결재란) 서명을 호출부에 알린다 — 위임 확정 시 그대로 저장해 두면
   * 다음 복원에서 정책 변경 여부를 대조할 수 있다.
   */
  onBaseSignature?: (signature: string) => void;
  /**
   * 편집 모달이 닫힌 후 호출되는 콜백 (외부 트리거 리셋 등에 사용).
   */
  onEditModalClose?: () => void;
  /**
   * 결재라인이 서버에 저장된 후 호출되는 콜백.
   * 상세 페이지에서 보고서를 재조회(loadReport)해 양식 결재란을 즉시 반영하는 데 사용.
   */
  onSaved?: () => void;
  /**
   * "이 현장 기본 결재선으로 저장"이 성공한 뒤 호출되는 콜백.
   * 부모가 `presetPreview`를 재조회해야 다음 "현장 기본으로 되돌리기"가 방금 저장한 값으로 간다
   * (preset은 부모가 마운트 시 1회 fetch한 값이라 저장 후 stale해지기 때문).
   */
  onSiteDefaultSaved?: () => void;
}

export function ApprovalLineSectionImpl({
  companyId,
  reportId,
  siteId,
  templateKey,
  presetPreview,
  approvalLines,
  onApprovalLinesChange,
  editable = true,
  isLoading = false,
  approvalSignatures = [],
  authorSignatureData,
  authorSignerName,
  onAuthorSignatureClick,
  maxApprovers,
  requiredFirstStep,
  resendApprovalInfo,
  isResendingApproval = false,
  onResendApproval,
  mode = 'report',
  chromeless = false,
  openEditTrigger = 0,
  parkedFillLevels = null,
  editModalIsSub = false,
  delegationSlot,
  restoreFrom = null,
  onBaseSignature,
  onEditModalClose,
  onSaved,
  onSiteDefaultSaved,
}: ApprovalLineSectionProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  // "이 현장 기본 결재선으로 저장" 체크박스 상태 — 기본 체크됨(모달 열 때마다 true 복원)
  const [saveAsSiteDefault, setSaveAsSiteDefault] = useState(true);
  // 회사 멤버 목록 — 결재자 후보(구 결재자 프로필 목록은 2026-09-16 E3/E4에서 은퇴)
  const [availableMembers, setAvailableMembers] = useState<OrganizationMember[]>([]);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  // report 모드에서도 메타 한도 파악을 위한 preview 데이터
  const [approvalLinePreview, setApprovalLinePreview] = useState<ApprovalLinePreviewData | null>(null);

  useEffect(() => {
    const check = () => setIsOverflow(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // 회사 멤버 목록 로드 (report/standalone 모드 공통)
  useEffect(() => {
    if (!companyId) return;
    setIsMembersLoading(true);
    organizationsApi.getMembers(companyId)
      .then((members) => setAvailableMembers(members))
      .catch(() => setAvailableMembers([]))
      .finally(() => setIsMembersLoading(false));
  }, [companyId]);

  /**
   * 복원된 결재자의 표시 정보 — 현재 멤버 목록에서 찾아 편집 경로와 **같은 규칙**으로 채운다.
   * 못 찾으면 undefined(그 사이 조직을 떠난 사람) — sourceType='member'와 함께 신원 유실로
   * 표시돼 재선택을 요구받는다.
   */
  const restoredMemberDisplay = (memberId: string, members: OrganizationMember[]) => {
    const om = members.find((m) => (m.member?.id ?? m.memberId ?? m.id) === memberId);
    return om ? extractMemberDisplay(om) : undefined;
  };

  // standalone 모드: P1 — 초기 결재라인 설정. 공급처는 주입 preview(presetPreview — 템플릿
  // approvalLineMeta cascade) **하나**다.
  // 종전의 `reportId` 갈래(`getReportApprovalLines`로 보고서별 행을 읽어 변환)는 2026-09-16 라운드 6에서
  // 삭제 — standalone 호출부는 위임 모달뿐이고(`ReportDelegationModal`) 거기서는 reportId를 넘기지 않아
  // 호출부 0이었다. 게다가 그 변환은 `sourceType`·`_assignmentChain`·`notifyVia`·`_locked`를 전파하지
  // 않아(S76-A 축 누락) 살려 두면 언젠가 반쪽 목록을 만든다. 상세(report 모드)의 같은 변환은
  // `loadReportApprovalLineItems`(useReportApproval)가 정본이다.
  useEffect(() => {
    if (mode !== 'standalone' || !companyId) return;
    // 외부에서 이미 approvalLines가 주입된 경우 초기화 스킵
    if (approvalLines.length > 0) return;
    // 현장 축이 없거나 정책이 없으면 초기화할 것이 없다.
    if (!siteId || !presetPreview?.hasApprovalLinePolicy) return;
    const preview = presetPreview;

    // 응답이 늦게 오는 동안 모달이 닫히면(부모가 상태를 리셋한 뒤) 여기서 setState가 나가
    // **리셋을 되돌린다** — 다음에 열 때 이전 결재라인이 남아 있고, 그러면 위 length>0 가드에
    // 걸려 초기화가 통째로 건너뛰어진다. 언마운트 후 갱신도 막는다.
    let cancelled = false;

    // 복원 결재자의 이름을 채우려면 멤버 목록이 **이 시점에** 있어야 한다. 별도 effect가
    // 채우는 availableMembers state에 기대면 그 응답이 늦을 때 member가 빈 채로 복원돼
    // 결재 칸에 단계명("2단계")이 뜬다(spec이 잡았다). 여기서 직접 확보한다.
    (restoreFrom
      ? organizationsApi.getMembers(companyId).catch(() => [] as OrganizationMember[])
      : Promise.resolve([] as OrganizationMember[])
    )
      .then((membersForRestore) => {
        if (cancelled) return;
        // 메타 cascade baseRows를 ApprovalLineItem으로 변환 (buildItemsFromPreviewBaseRows 사용)
        const items = buildItemsFromPreviewBaseRows(preview);
        // 지난 위임에서 쓴 추가 결재자를 고정 결재란 뒤에 잇는다. 정책이 바뀌었거나 한도가
        // 줄었으면 restorableAdditionalLines가 걸러 빈 배열을 준다(조직 기본이 이긴다).
        // 판정은 preview를 가진 이쪽 몫이다 — 호출부(위임 모달)는 baseRows로 판정하지 않는다.
        const signature = approvalBaseSignature(preview);
        onBaseSignature?.(signature);
        // 한도 축은 편집 모달과 같아야 한다(effectiveMaxApprovers) — 총 허용은
        // locked 칸 + **양식 단독** 비-locked 칸(baseNonLockedCount) + maxAdditionalLines이고,
        // 이미 만든 items를 빼면 남은 복원 여유가 된다. 축이 어긋나면 복원 직후 한도를
        // 넘긴 상태가 되어(모달을 열면 그제야 거부) 사용자가 원인을 알 수 없다.
        const lockedCount = preview.baseRows.filter((r) => r.locked).length;
        const restoreLimit = Math.max(
          0,
          lockedCount +
            (preview.baseNonLockedCount ?? 0) +
            preview.maxAdditionalLines -
            items.length,
        );
        const restored = restorableAdditionalLines(
          restoreFrom ?? null,
          signature,
          items.length,
          restoreLimit,
        ).map((line) => ({
          memberId: line.memberId,
          // 이름 자리를 채우려면 member가 있어야 한다 — 없으면 buildMergedItems가 label을
          // 이름으로 써서 결재 칸에 "2단계"가 뜬다. 저장분엔 이름을 넣지 않으므로(그 사이
          // 바뀌었을 수 있다) 현재 멤버 목록에서 찾아 채운다.
          member: line.memberId
            ? restoredMemberDisplay(line.memberId, membersForRestore)
            : undefined,
          // sourceType이 없으면 신원 유실 판정(sourceType==='member')이 이 행을 그냥 지나쳐,
          // 사라진 결재자가 빈 칸으로 남은 채 재선택을 요구하지 않는다.
          sourceType: line.memberId ? ('member' as const) : undefined,
          approvalLevel: line.approvalLevel,
          label: line.label,
          // 채널은 정본 축(notifyVia) 하나 — 복원 경계(restorableAdditionalLines)가 구
          // 저장분의 객체 축까지 notifyVia로 정규화해 돌려주므로 그대로 싣는다(F-J 왕복 보존).
          notifyVia: line.notifyVia as ApprovalLineItem['notifyVia'],
        }));
        const merged = [...items, ...restored];
        if (merged.length > 0) {
          onApprovalLinesChange(merged);
        }
      })
      .catch(() => {/* 초기화 실패 시 빈 상태 유지 */});

    return () => {
      cancelled = true;
    };
    // approvalLines 의존성만 제외(초기 1회만 — 재실행 시 위 length>0 가드로 편집 덮어쓰기 방지).
    // presetPreview는 deps에 포함(마운트 후 preset 변경 시 최신값 반영 — set-once라 churn 없음).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, companyId, siteId, presetPreview]);

  // report 모드: 편집 모달 한도 산정용 preview — 주입 preset(템플릿 approvalLineMeta)을 state로 동기화.
  // 되돌리기(handleResetToSiteDefault)의 클릭 시점 재조회가 이 state를 갱신하므로 preset prop보다
  // 신선할 수 있다(effectiveMaxApprovers 주석). 별도 메타 fetch 갈래는 두지 않는다 —
  // 결재 정책의 정본은 템플릿 approvalLineMeta + 현장 override다.
  useEffect(() => {
    if (mode !== 'report' || !presetPreview) return;
    setApprovalLinePreview(presetPreview);
  }, [mode, presetPreview]);

  // 현장 메타(SiteApprovalLineMeta.templateKey)의 축 값 = 템플릿 key.
  // templateKey가 없으면 "무엇에 저장할지"를 알 수 없어 저장 자체를 열지 않는다.
  const siteMetaKey: string | undefined = templateKey;
  // 현장 기본 저장 가능 여부.
  const canSaveSiteDefault = !!siteId && !!siteMetaKey;

  /**
   * 칸별 담당자 소스 화이트리스트 — 편집 모달이 허용되지 않는 선택지를 아예 내놓지 않게 한다.
   *
   * 정본 우선순위는 effectiveMaxApprovers와 같다(report 모드는 재조회로 갱신되는 state가
   * preset prop보다 신선하다). base 범위 밖(작성자 추가분)은 배열이 짧아 제한 없음이 된다.
   *
   * 이 배열의 축은 preview `baseRows`의 **index**라, 편집 중 행을 지우거나 옮기면 어긋난다.
   * 그래서 모달에 그대로 넘기지 않고 편집 목록을 여는 순간(`initEdit`) 각 행에 새겨,
   * 이후에는 행과 함께 움직이게 한다(`useApprovalLineEdit`의 `_assigneeSourceWhitelist`).
   */
  const assigneeSourceWhitelists: (('delegatedTo' | 'role' | 'author' | 'member')[] | undefined)[] = (() => {
    const source =
      mode === 'report' && approvalLinePreview?.hasApprovalLinePolicy
        ? approvalLinePreview
        : presetPreview?.hasApprovalLinePolicy
          ? presetPreview
          : null;
    return source ? source.baseRows.map((row) => row.assigneeSourceWhitelist) : [];
  })();

  // PENDING_APPROVAL 대기 칸 지정 모드 — 목록을 "대기 칸만 열린" 형상으로 바꿔 편집 훅에 준다.
  const parkedFillMode = !!parkedFillLevels && parkedFillLevels.length > 0;
  const editSourceLines = useMemo(() => {
    if (!parkedFillMode) return approvalLines;
    const parkedLevelSet = new Set(parkedFillLevels ?? []);
    return approvalLines.map((line) => {
      if (!parkedLevelSet.has(line.approvalLevel)) return line;
      // 대기 칸: 정의 컬럼에 남은 옛 신원(떠난 사람)을 지워 **미해소 칸 형상**으로 만든다 —
      // 편집 모달은 미해소 칸에만 지정 select를 내놓는다(S66 공유 select). park는 서명행만
      // 비우고 정의는 그대로 두므로, 정의 기반 목록에는 떠난 사람이 지정된 것처럼 남아 있다.
      // `sourceType`은 비운다 — 'member'인 채 id만 지우면 신원 유실 가드(isIdentityLost)가
      // 저장을 막는다. 체인은 미해소 판정(isUnresolvedApprovalStep)의 축이라 남긴다(없으면
      // 최소 형상으로 채운다 — 정의 없는 레거시 행도 select가 떠야 한다).
      const chain = line._assignmentChain;
      return {
        ...line,
        memberId: undefined,
        member: undefined,
        sourceType: undefined,
        _assignmentChain:
          chain && chain.length > 0
            ? chain
            : [{ type: 'member' as const, memberId: line.memberId ?? '' }],
        _assigneeUnset: false,
        // 이 행이 "지정을 받는 대기 칸"임을 모달에 알린다 — 지정 후에도 select를 유지해
        // 다시 고를 수 있게 하고(해제 없이 교체), 나머지 행과의 잠금 판정을 가른다.
        _parkedFill: true,
      } as ApprovalLineItem;
    });
  }, [approvalLines, parkedFillMode, parkedFillLevels]);

  const editHook = useApprovalLineEdit(editSourceLines, availableMembers, assigneeSourceWhitelists);
  const memberFormHook = useApproverMemberForm(
    companyId,
    editHook.editingLines,
    editHook.setEditingLines,
    async () => {
      // 새 멤버 등록 후 멤버 목록 갱신
      const members = await organizationsApi.getMembers(companyId);
      setAvailableMembers(members);
    },
  );

  const handleOpenEditModal = useCallback(() => {
    editHook.initEdit();
    setShowEditModal(true);
  // editHook.initEdit은 안정적 참조이므로 의존성 배열에 포함
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editHook.initEdit]);

  const handleCloseEditModal = useCallback(() => {
    editHook.resetEdit();
    setSaveError(null);
    setSaveAsSiteDefault(true); // 다음 열 때도 기본 체크 상태로 복원
    setShowEditModal(false);
    onEditModalClose?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editHook.resetEdit, onEditModalClose]);

  // 외부 트리거(헤더 버튼)로 편집 모달 열기
  const prevOpenEditTriggerRef = useRef(openEditTrigger);
  useEffect(() => {
    if (openEditTrigger > 0 && openEditTrigger !== prevOpenEditTriggerRef.current) {
      prevOpenEditTriggerRef.current = openEditTrigger;
      handleOpenEditModal();
    }
  }, [openEditTrigger, handleOpenEditModal]);

  /**
   * editingLines → ApprovalLineMetaRow[] 변환 헬퍼 (옵션C: 보고서 편집 → site override 변환).
   * _locked=true인 행은 admin base 고정 단계이므로 label/assignmentChain을 변경하지 않고
   * preview.baseRows의 원본 값을 그대로 보존한다 (서버 enforceLockedFromSystem도 재교정하지만
   * 클라 변환도 일관되게 locked 보존).
   * _locked=false 또는 추가 단계는 편집값 그대로 사용.
   */
  const toApprovalLineMetaRows = (lines: ApprovalLineItem[]): ApprovalLineMetaRow[] =>
    lines.map((line, i) => {
      const locked = (line as ApprovalLineItem & { _locked?: boolean })._locked === true;
      // 담당자를 뗀 칸은 locked 여부와 무관하게 **빈 체인**으로 저장한다.
      // locked는 칸 구조에 대한 권한이지 담당자에 대한 것이 아니므로(결정 2026-08-13 §7),
      // 아래 locked 분기가 base 원본 담당자를 되살리면 해제가 조용히 되돌아간다.
      // 서버 enforceLockedFromSystem도 "하위가 비운 것은 정상 상태"로 보존한다.
      if (line._assigneeUnset === true) {
        return {
          level: i + 1,
          label: (locked && approvalLinePreview?.baseRows[i]?.label) || line.label || '',
          locked,
          assignmentChain: [],
        };
      }

      if (locked && approvalLinePreview?.baseRows[i]) {
        // locked 단계: preview base 원본 값 보존 (label/assignmentChain을 admin 의도대로 유지)
        const baseRow = approvalLinePreview.baseRows[i];
        // ⚠️ **memberId 유무로 판정하면 작성자 단계가 개인으로 굳는다.**
        // author 단계의 baseRow는 memberId가 *채워져서* 온다(preview가 작성자를 resolve한 결과).
        // 게다가 preview 엔드포인트는 신규 작성 문맥에서 **현재 로그인 사용자**를 작성자로 쓰므로,
        // 작성자가 아닌 사람이 현장 기본 결재선을 저장하면 그 칸이 `member/편집자`로 박히고,
        // 이후 그 현장의 모든 보고서가 1단계를 **편집자**로 해소한다
        // (2026-08-13 dev에서 실제 발생: `과천시 과천읍/SAFETY_INSPECTION` 1단계가
        //  `{type:'author'}` → `{type:'member', 임경업}`으로 오염).
        // `sourceType`은 바로 이 구분을 위해 preview 응답에 실려 온다(lib/reports.ts 주석) —
        // memberId fallback보다 **먼저** 본다.
        // 메타가 담당자를 비워 둔 칸(미지정 = 오프라인 수기)은 비운 채로 저장한다.
        // memberId fallback만 있던 옛 코드는 이 칸에 `author`를 채워 넣어, 저작자가 비워 둔
        // 칸에 작성자가 조용히 들어앉았다.
        //
        // S76-A: 메타 **원본 체인**이 preview에 실려 오면 그것을 그대로 쓴다. 위 파생은
        // 해소 결과(sourceType/memberId)로 체인을 *다시 짓는* 것이라, 역할·위임 칸이
        // `[{member}]`나 `[{author}]`로 굳고 2단 폴백 사다리는 꼬리를 잃었다.
        const baseUnassigned = baseRow.assigneeUnset === true;
        const baseChain = baseRow.assignmentChain;
        const assignmentChain: ApprovalLineMetaRow['assignmentChain'] = baseUnassigned
          ? []
          : baseChain && baseChain.length > 0
            ? baseChain
            : baseRow.sourceType === 'author'
              ? [{ type: 'author' as const }]
              : baseRow.memberId
                ? [{ type: 'member', memberId: baseRow.memberId }]
                : [{ type: 'author' as const }];
        return {
          level: i + 1,
          label: baseRow.label,
          locked: true,
          assignmentChain,
        };
      }

      // unlocked 단계 또는 preview base 정보 없는 경우: 편집값 사용.
      // member 단계는 memberId를 끝까지 보존 — 이름 매칭 실패로 author chain으로
      // 떨어지면 site 메타가 '현장소장→author'로 오염되므로 sourceType을 우선 판정한다.
      // 같은 이유로 **memberId가 아예 없는 member 단계(신원 유실 백필분)도 author로 떨어뜨리지
      // 않는다** — handleSaveEdit이 이미 저장을 막지만(재선택 요구), 여기서도 빈 chain으로 두어
      // 어떤 경로로 흘러들어와도 '결재자 → author' 오염이 생기지 않게 한다.
      // 빈 chain은 위임 결재라인이 이미 쓰는 형태로, 재resolve 시 "빈 슬롯"이 된다.
      //
      // S76-A: **손대지 않은 칸은 불러올 때의 체인을 통째로 보존한다.** 담당자를 실제로
      // 바꾸면 편집 훅(handleAssignMember/handleAssignAuthor/handleUnsetAssignee)이
      // `_assignmentChain`을 지우므로, 남아 있다는 것 자체가 "안 바꿨다"는 뜻이다.
      // 이 보존이 없으면 역할·위임 칸이 편집 모달 왕복만으로 사람/작성자로 굳고
      // (sourceType은 해소 결과라 폴백된 값이 되돌아온다), 2단 사다리는 머리만 남는다.
      const loadedChain = line._assignmentChain;
      const keepsLoadedChain = !!loadedChain && loadedChain.length > 0;

      const isAuthorStep = line.sourceType === 'author' && !line.memberId;
      const assignmentChain: ApprovalLineMetaRow['assignmentChain'] = isIdentityLostApprovalStep(line)
        ? []
        : keepsLoadedChain
          ? loadedChain!
          : !isAuthorStep && line.memberId
            ? [{ type: 'member', memberId: line.memberId }]
            : [{ type: 'author' as const }];
      return {
        level: i + 1,
        label: line.label ?? '',
        locked,
        assignmentChain,
      };
    });

  const handleSaveEdit = async () => {
    // 결재자 신원이 유실된 단계는 저장하지 않는다. 이 행은 이름이 정상으로 보이지만
    // (서명행에서 복원된 표시값) memberId가 없어 저장 변환에서 author 단계로 강등된다 —
    // 2026-07-24 "조용한 스킵"과 같은 부류라, 값을 지어내는 대신 재선택을 요구한다.
    const identityLostIndex = findIdentityLostApprovalStep(editHook.editingLines);
    if (identityLostIndex >= 0) {
      setSaveError(identityLostApprovalStepMessage(editHook.getStepLabel(identityLostIndex)));
      return;
    }

    // 담당자 지정은 앞에서부터 연속이어야 한다(결정 2026-08-13 §2). 앞 칸을 비우면 뒤 칸의
    // 지정이 무효가 되는데, **남의 입력을 말없이 지우지 않는다** — 자동 해제 대신 저장을
    // 막고 사유를 보여준다(어느 쪽을 고칠지는 사용자가 정한다).
    // 서버도 같은 규칙으로 400(APPROVAL_LINE_ASSIGNEE_ORDER_VIOLATION)을 내지만, 저장을
    // 눌러야 알게 되는 것을 여기서 미리 막는다.
    const prefixViolationIndex = findApprovalPrefixViolation(editHook.editingLines);
    if (prefixViolationIndex >= 0) {
      setSaveError(approvalPrefixViolationMessage(editHook.getStepLabel(prefixViolationIndex)));
      return;
    }

    // 대기 칸 지정 모드에서는 부모 상태를 편집 목록으로 덮지 않는다 — 이 목록은 "대기 칸만
    // 열린" 변형 형상이라 표시 정본이 아니고, 저장 성공 시 onSaved(loadReport)가 재조회한다.
    if (!parkedFillMode) onApprovalLinesChange(editHook.editingLines);
    setSaveError(null);
    if (mode === 'standalone') {
      // P1: 메타 cascade 모드에서는 사이트 레벨 저장 없이 상태만 업데이트.
      // 보고서 생성 payload(additionalApprovalLines)는 외부(write/new 컴포넌트)에서 조합.
      setSaveAsSiteDefault(true);
      setShowEditModal(false);
      return;
    }
    setIsSaving(true);
    try {
      // 구 DLR 전용 저장 분기(setDlrApprovalLines)는 B1-DLR-6에서 제거 — 전환본은 아래
      // 전체저장 모델을 탄다.
      if (reportId && companyId) {
        // 전체저장 모델: 편집 모달의 전체 결재라인을 순서대로 통째 전송.
        // locked 단계도 포함하되 서버가 메타 권위값으로 교정(클라 locked 사칭 무시).
        // 추가분 분리/합치기 없음 → 누적 버그 원천 차단.
        await setReportApprovalLines(
          reportId,
          companyId,
          toFullLinesPayload(editHook.editingLines),
        );
      }

      // 결재라인 서버 저장 성공 → 부모에 보고서 재조회 요청(양식 결재란 즉시 반영, 새로고침 불필요)
      onSaved?.();

      // "이 현장 기본 결재선으로 저장" 체크박스가 선택된 경우 site override 갱신.
      // 저장 축(templateKey)은 siteMetaKey(= 템플릿 key).
      // 대기 칸 지정 모드는 제외 — 진행 중 문서의 칸 하나를 채운 형상(신원 지운 변형 목록)이
      // 현장 기본 결재선을 덮어쓰면 안 된다(체크박스 자체를 숨긴다).
      if (!parkedFillMode && saveAsSiteDefault && canSaveSiteDefault && siteId && siteMetaKey) {
        // 한도(maxAdditionalLines)·정책 유무는 양식 단독 권위라 body에 싣지 않는다(R1 — 서버 DTO whitelist, 보내면 400).
        const dto: UpdateSiteApprovalLineMetaDto = {
          lines: toApprovalLineMetaRows(editHook.editingLines),
          reason: '보고서 편집에서 현장 기본 결재선 갱신',
        };
        try {
          await approvalLineMetaApi.updateSiteMeta(siteId, siteMetaKey, dto);
          // 부모 preset(마운트 시 1회 fetch) 재조회 요청 — 다음 되돌리기가 방금 저장한 현장 기본으로 가도록.
          onSiteDefaultSaved?.();
        } catch (siteErr) {
          // site override 실패는 보고서 저장 성공과 별개로 처리
          console.error('현장 기본 결재선 저장 실패:', siteErr);
          // 부분 성공(보고서는 저장됨)이라는 사실이 핵심 정보라 기존 문구를 유지하고,
          // 서버가 지목한 사유가 있으면 뒤에 덧붙인다.
          const siteReason = apiErrorMessage(siteErr);
          setSaveError(
            siteReason
              ? `보고서 결재선은 저장되었으나 현장 기본 결재선 갱신에 실패했습니다. — ${siteReason}`
              : '보고서 결재선은 저장되었으나 현장 기본 결재선 갱신에 실패했습니다.',
          );
          setSaveAsSiteDefault(true);
          setIsSaving(false);
          return;
        }
      }

      setSaveAsSiteDefault(true);
      setShowEditModal(false);
    } catch (err) {
      console.error('결재라인 저장 실패:', err);
      // 서버가 거부 사유를 칸까지 지목해 준 경우 그대로 보여준다 — 일반 문구로 덮으면
      // 어느 칸을 고쳐야 하는지 알 수 없다(S3·S65).
      setSaveError(apiErrorMessage(err) ?? '결재라인 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * editingLines → 전체저장 입력 라인 변환 헬퍼.
   * handleSaveEdit와 handleResetToSiteDefault에서 공통 사용.
   */
  const toFullLinesPayload = (lines: ApprovalLineItem[]) =>
    lines.map((line, idx) => {
      const item = line as ApprovalLineItem & { _locked?: boolean };
      // 담당자를 뗀 칸 — 칸만 남기고 담당자 관련 값은 전부 비운다. assigneeUnset이 없으면
      // 서버가 이 행을 "빈 행"으로 보고 스킵해 **칸 자체가 사라진다**(sourceType fallback이
      // 'author'로 떨어지면 반대로 작성자 칸으로 둔갑한다). 두 사고 모두 이 플래그로 막는다.
      if (item._assigneeUnset === true) {
        return {
          approvalLevel: idx + 1,
          label: line.label,
          locked: item._locked === true,
          assigneeUnset: true,
        };
      }
      // S76-A: **지정 방식을 접지 않고 그대로 보낸다.**
      //
      // 종전에는 author가 아니면 memberId 유무로 member/author 둘 중 하나로 접었고, 그래서
      // 역할·위임으로 정의된 칸을 손대지 않고 저장해도 *그 근거가 뽑아낸 사람*이 `member`로
      // 되돌아가 서버에서 근거가 지워졌다. 서버는 role/delegatedTo 선언을 그 칸의 권위 체인과
      // 대조해 일치할 때만 채택하므로(임의 역할 주입 불가) 여기서는 정직하게 말하면 된다.
      //
      // 손대지 않은 칸의 지정 방식은 **체인 머리**가 정본이다. `item.sourceType`은 해소
      // 결과라, 역할·위임이 해소되지 않은 칸은 폴백 종류('author')로 내려앉아 있다 — 그걸
      // 그대로 보내면 서버가 "사용자가 작성자를 골랐다"로 읽어 체인이 영구히 굳는다.
      const declaredHead = item._assignmentChain?.[0];
      const declaredDerived =
        declaredHead && (declaredHead.type === 'role' || declaredHead.type === 'delegatedTo')
          ? declaredHead
          : undefined;
      //
      // memberId 없는 member 단계가 'author'로 떨어지는 것은 2026-07-24 사고의 재발 경로다.
      // handleSaveEdit이 저장 전에 그런 행을 걸러 내므로(신원 유실 → 저장 차단 + 재선택 요구)
      // 이 fallback에는 도달하지 않는다. 판정을 바꾸려면 그 가드부터 함께 볼 것.
      const sourceType: 'delegatedTo' | 'role' | 'author' | 'member' = declaredDerived
        ? declaredDerived.type
        : item.sourceType === 'author'
          ? 'author'
          : item.memberId
            ? 'member'
            : 'author';
      return {
        memberId: line.memberId || undefined,
        approvalLevel: idx + 1,
        label: line.label,
        notifyVia: line.notifyVia,
        sourceType,
        // 역할 칸은 **어떤 역할인지**까지 보내야 서버가 권위 체인과 대조할 수 있다.
        assignmentRole: declaredDerived?.type === 'role' ? declaredDerived.role : undefined,
        locked: item._locked === true,
      };
    });

  /**
   * "현장 기본으로 되돌리기": **편집 모달의 로컬 편집 상태(editingLines)만** 현장 cascade
   * 기본으로 되돌린다. 서버 저장/보고서 반영은 하지 않는다 — 사용자가 '저장'을 눌러야
   * handleSaveEdit이 editingLines를 통째 저장해 반영되고, 그냥 닫으면(취소) resetEdit으로
   * 폐기되어 반영되지 않는다. 모달은 닫지 않는다.
   *
   * presetPreview 주입: preset이 이미 현장 오버라이드 → 템플릿 approvalLineMeta cascade를 거친
   *   값이므로 그대로가 "현장 기본"이다(P3).
   * preset 미주입: 정책 없음 — 빈 배열로 편집 상태 교체(cascade 재조회 갈래를 두지 않는다).
   */
  const handleResetToSiteDefault = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      // 구 DLR 분기(cascade 없음 → 빈 배열)는 B1-DLR-6에서 제거.
      if (presetPreview) {
        // 되돌리기 기준은 "지금의 현장 기본"이므로 **클릭 시점에 재조회**한다. 주입 preset은 모달
        // 오픈 시점 값이라, 모달이 열려 있는 동안 현장 상세에서 현장 기본이 바뀌면 stale —
        // 재조회 실패 시에만 폴백으로 쓴다.
        let effectivePreset = presetPreview;
        if (templateKey && siteId) {
          try {
            effectivePreset = await fetchComposedApprovalLinePreview(templateKey, siteId);
            // locked 행 보존(toApprovalLineMetaRows)·한도 산정도 최신 기준으로 갱신
            setApprovalLinePreview(effectivePreset);
          } catch {
            // 재조회 실패 시 주입 preset 유지
          }
        }
        editHook.setEditingLines(
          effectivePreset.hasApprovalLinePolicy && effectivePreset.baseRows.length > 0
            ? buildItemsFromPreviewBaseRows(effectivePreset)
            : [],
        );
      } else {
        // preset 미주입 = 정책 없음: 편집 상태만 비운다(저장 전까지 미반영).
        editHook.setEditingLines([]);
      }
      // 편집 모달 안에서만 되돌림 — 서버 저장/보고서 반영은 '저장'을 눌러야 일어난다.
      // (그냥 닫으면 handleCloseEditModal의 resetEdit으로 폐기되어 반영되지 않음)
    } catch (err) {
      console.error('현장 기본으로 되돌리기 실패:', err);
      setSaveError('되돌리기에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // effectiveMaxApprovers: 편집 모달에서 허용하는 결재 단계 총수 상한.
  //
  // 서버 판정(setReportApprovalLines §5)과 같은 축이어야 한다:
  //   비-locked 총수 ≤ **양식 단독** 비-locked 칸 수(baseNonLockedCount) + maxAdditionalLines
  // → 총 허용 = lockedCount + baseNonLockedCount + maxAdditionalLines.
  //
  // ⚠️ `baseNonLockedCount` 대신 `baseRows`의 비-locked 수를 세면 안 된다 — baseRows는 현장
  // 오버라이드가 섞인 병합본이라, 현장이 자기 기본 결재선에 넣은 칸까지 면제되어 화면은
  // 허용하는데 서버가 400을 낸다(그 구멍을 막는 것이 base를 양식 단독으로 둔 이유다).
  //
  // 종전(모델 B)은 `lockedCount + maxAdditionalLines`라 양식이 준 편집 허용 칸이 한도를
  // 먼저 소비했다 — 그 판정은 양식 그대로도 저장 400을 내는 자기모순이라 폐기됐다(2026-08-13).
  // standalone 모드는 외부 prop(maxApprovers)만 사용.
  const effectiveMaxApprovers: number | undefined = (() => {
    // report 모드는 approvalLinePreview state가 정본 — 주입 preset(presetPreview)과 동기화되고
    // (`setApprovalLinePreview(presetPreview)` 동기화 effect), "현장 기본으로 되돌리기"
    // (`handleResetToSiteDefault`)의 클릭 시점 재조회로 preset보다 신선할 수 있다.
    // preset prop을 먼저 보면 되돌리기 이후 한도가 모달 오픈 시점 값에 머문다(리뷰 W4).
    // `?? 0`은 옛 서버(필드 미전송) 대비 — 없으면 종전 모델과 같은(더 좁은) 상한이 되어
    // 안전하게 퇴화한다. 산술에 undefined가 섞여 NaN이 되면 상한 판정이 통째로 무력화된다.
    if (mode === 'report' && approvalLinePreview?.hasApprovalLinePolicy) {
      const lockedCount = approvalLinePreview.baseRows.filter((r) => r.locked).length;
      return (
        lockedCount +
        (approvalLinePreview.baseNonLockedCount ?? 0) +
        approvalLinePreview.maxAdditionalLines
      );
    }
    // standalone(또는 state 미동기 첫 렌더): 주입 preset이 정본.
    if (presetPreview?.hasApprovalLinePolicy) {
      const lockedCount = presetPreview.baseRows.filter((r) => r.locked).length;
      return (
        lockedCount + (presetPreview.baseNonLockedCount ?? 0) + presetPreview.maxAdditionalLines
      );
    }
    return maxApprovers;
  })();

  // effectiveMaxAdditionalLines: 양식이 정의한 칸 **외에** 더 만들 수 있는 칸 수.
  // EditModal "최대 N명까지 추가 가능합니다" 안내 문구의 N으로만 사용(판정은 effectiveMaxApprovers).
  // report 모드: preview.maxAdditionalLines 직접 사용.
  // standalone 모드(preview 없음): maxApprovers에서 locked 첫 단계 1칸을 뺀 값으로 fallback.
  const effectiveMaxAdditionalLines: number | undefined = (() => {
    // effectiveMaxApprovers와 동일 우선순위 — report 모드는 state(재조회 최신) 우선.
    if (mode === 'report' && approvalLinePreview?.hasApprovalLinePolicy) {
      return approvalLinePreview.maxAdditionalLines;
    }
    if (presetPreview?.hasApprovalLinePolicy) {
      return presetPreview.maxAdditionalLines;
    }
    if (maxApprovers === undefined) return undefined;
    const lockedFirst = requiredFirstStep?.locked ? 1 : 0;
    return Math.max(0, maxApprovers - lockedFirst);
  })();

  const handleAddDropdownChange = (value: string) => {
    if (value === '__new__') {
      memberFormHook.openCreateModal();
    } else if (value) {
      if (effectiveMaxApprovers !== undefined && editHook.editingLines.length >= effectiveMaxApprovers) {
        return;
      }
      // 추가는 목록 끝에 **지정된** 칸을 만든다 — 앞에 미지정 칸이 있으면 그 자체가 prefix
      // 위반이다. 모달에서 이미 비활성이지만, 상태가 어긋난 경로로 들어와도 만들지 않는다.
      if (editHook.editingLines.some((line) => line._assigneeUnset === true)) {
        return;
      }
      editHook.handleAddMember(value);
    }
  };

  /**
   * 빈 결재란(담당자 미지정) 추가 — 칸 수 상한만 본다.
   * prefix 가드는 붙이지 않는다: 빈 칸은 목록 끝에 붙으므로 "미지정 뒤에 지정"이 생기지 않는다.
   */
  const handleAddUnassignedSlot = () => {
    if (
      effectiveMaxApprovers !== undefined &&
      editHook.editingLines.length >= effectiveMaxApprovers
    ) {
      return;
    }
    editHook.handleAddUnassignedSlot();
  };

  // 이미 추가된 memberId Set (중복 방지)
  const addedMemberIds = new Set(editHook.editingLines.map((l) => l.memberId).filter(Boolean));
  // 아직 추가 안 된 회사 멤버만 후보로.
  // S17-c: **탈퇴한 계정은 후보에서 뺀다** — `getMembers`는 멤버 관리 화면이 '탈퇴' 배지를
  // 그려야 해서 탈퇴 계정을 응답에 남기므로, 결재자 후보는 여기서 걸러야 한다(R6 실측: 그대로
  // 두면 "최탈퇴 · Email"이 담당자 지정 select에 떴다). 계정 없는 인원은 탈퇴가 아니므로
  // 남는다(S18 최소장). 서버 저장 경로도 같은 술어로 거부한다(APPROVAL_LINE_ASSIGNEE_WITHDRAWN).
  const filteredMembers = availableMembers.filter(
    (m) => !isWithdrawnMember(m) && !addedMemberIds.has(m.member?.id ?? m.memberId ?? m.id),
  );

  // 양식 밖 진입점 유무 — 편집 버튼(editable) 또는 재전송 버튼(canResend) 중 하나라도 있는지.
  const hasResendEntry = !!(resendApprovalInfo?.canResend && resendApprovalInfo.currentApprover && onResendApproval);
  const hasAnyEntry = editable || hasResendEntry;

  // chromeless 모드(양식 본문이 결재란 표시 담당): 양식 밖에 보여줄 진입점(편집/재전송)이
  // 하나도 없으면 빈 버튼 영역이 남지 않도록 섹션 전체를 렌더하지 않는다(Step D dev 피드백).
  // 로딩 중에는 스피너를 보여야 하므로 가드에서 제외한다.
  if (chromeless && !hasAnyEntry && !(isLoading || isMembersLoading)) {
    return null;
  }

  // 편집/프로필 모달 — 카드/chromeless 양쪽 return에서 공통 사용.
  const modals = (
    <>
      {showEditModal && (
        <EditModal
          // 층은 여는 쪽이 주입한다(모달 z 정책) — 이 섹션이 위임 모달(z-[80]) 안에 있으면
          // 편집 모달은 sub2(z-[90]), 단독(상세 헤더)이면 root(z-[70]). 겹침 층은 오버레이
          // 알파가 곱해지지 않게 옅은 배경을 함께 준다.
          overlayZClassName={editModalIsSub ? MODAL_LAYER.sub2 : MODAL_LAYER.root}
          overlayBgClassName={editModalIsSub ? SUB_MODAL_OVERLAY_BG : undefined}
          editingLines={editHook.editingLines}
          availableMembers={filteredMembers}
          isSaving={isSaving}
          isDirty={editHook.isDirty}
          draggedIndex={editHook.draggedIndex}
          dragOverIndex={editHook.dragOverIndex}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit}
          onAddDropdownChange={handleAddDropdownChange}
          onAddUnassignedSlot={handleAddUnassignedSlot}
          onRemoveApprover={editHook.handleRemoveApprover}
          onUnsetAssignee={editHook.handleUnsetAssignee}
          onAssignMember={editHook.handleAssignMember}
          onFixMemberContact={memberFormHook.openContactFixModal}
          onAssignAuthor={editHook.handleAssignAuthor}
          onMoveUp={editHook.handleMoveUp}
          onMoveDown={editHook.handleMoveDown}
          onDragStart={editHook.handleDragStart}
          onDragEnd={editHook.handleDragEnd}
          onDragOver={editHook.handleDragOver}
          onDragLeave={editHook.handleDragLeave}
          onDrop={editHook.handleDrop}
          onLabelChange={editHook.handleLabelChange}
          onToggleNotify={editHook.handleToggleNotify}
          getStepLabel={editHook.getStepLabel}
          getDefaultLabel={editHook.getDefaultLabel}
          maxApprovers={effectiveMaxApprovers}
          maxAdditionalLines={effectiveMaxAdditionalLines}
          requiredFirstStep={requiredFirstStep}
          hideFirstLineName={mode === 'standalone' && requiredFirstStep?.locked === true}
          errorMessage={saveError}
          parkedFillMode={parkedFillMode}
          delegationSlot={delegationSlot}
          // 대기 칸 지정 모드에서는 현장 기본 저장·되돌리기를 숨긴다 — 변형 목록(대기 칸의
          // 신원을 지운 형상)이 현장 기본을 덮거나, 되돌리기가 진행 중 결재선을 갈아치우면
          // 안 된다(서버도 대기 칸 외 접촉을 거부한다).
          showSaveAsSiteDefault={mode === 'report' && canSaveSiteDefault && !parkedFillMode}
          saveAsSiteDefault={saveAsSiteDefault}
          onToggleSaveAsSiteDefault={setSaveAsSiteDefault}
          showResetToSiteDefault={mode === 'report' && !!reportId && !parkedFillMode}
          onResetToSiteDefault={handleResetToSiteDefault}
        />
      )}

      {/* 결재라인 편집 모달 위에 겹쳐 뜨는 sub 모달.
          신규 등록과 **연락처 보정**(F-G) 두 모드를 공유한다(제목·설명만 다르다). 전화번호는
          양쪽 다 필수 — 전화번호는 신원 키이고, 보정 목적 자체가 도달 수단 확보다. */}
      <ApproverContactFormModal
        open={memberFormHook.showModal}
        isSubModal
        // 이 폼은 **편집 모달 위에서** 열린다 — 편집 모달이 sub2(위임 흐름)로 올라가면 그보다
        // 위여야 한다. 기본 sub로 두면 편집 모달 뒤로 숨는다(2026-08-11 z층 회귀).
        layer={editModalIsSub ? MODAL_LAYER.sub3 : MODAL_LAYER.sub}
        heading={memberFormHook.isFixingContact ? '담당자 연락처 입력' : '현장·업체 담당자 추가'}
        description={
          memberFormHook.isFixingContact
            ? '연락처가 없어 결재 요청을 보낼 수 없는 담당자입니다. 연락처를 입력하면 그대로 지정됩니다.'
            : '현장 또는 업체 담당자를 등록합니다. 등록 후 결재라인에 추가됩니다.'
        }
        value={memberFormHook.form}
        error={memberFormHook.error}
        isSaving={memberFormHook.isSaving}
        onChange={memberFormHook.onFormChange}
        onSave={memberFormHook.handleSave}
        onClose={memberFormHook.closeModal}
      />
    </>
  );

  // chromeless: 카드 컨테이너 없이 버튼만(편집/재전송) 인라인 렌더 + 모달.
  // 상세 페이지가 양식 본문만 깔고 결재라인 상호작용을 버튼 → 모달로 처리할 때 사용.
  if (chromeless) {
    return (
      <div className="flex items-center gap-2">
        {editable && (
          <button
            type="button"
            onClick={handleOpenEditModal}
            disabled={isLoading || isMembersLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-card-sm hover:bg-brand-100 transition-colors disabled:opacity-50"
            title="결재라인 편집"
          >
            <EditIcon className="w-3.5 h-3.5" />
            결재라인 편집
          </button>
        )}
        {hasResendEntry && (
          <button
            type="button"
            onClick={onResendApproval}
            disabled={isResendingApproval}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-700 bg-white border border-line rounded-card-sm hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            title="결재 링크를 재전송합니다 (SMS/이메일)"
          >
            {isResendingApproval ? (
              <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            결재 링크 재전송
          </button>
        )}
        {modals}
      </div>
    );
  }

  return (
    <Card padding="xl" elevation="none">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900">결재라인</h3>
        {editable && (
          <button
            type="button"
            onClick={handleOpenEditModal}
            className="p-1.5 text-ink-400 hover:text-brand-600 rounded hover:bg-brand-50 transition-colors"
            title="결재라인 수정"
          >
            <EditIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {isLoading || isMembersLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
          <span className="ml-2 text-sm text-ink-500">결재라인 불러오는 중...</span>
        </div>
      ) : (
        <div>
          <ApprovalDisplayTable
            approvalLines={approvalLines}
            approvalSignatures={approvalSignatures}
            isOverflow={isOverflow}
            authorSignatureData={authorSignatureData}
            authorSignerName={authorSignerName}
            onAuthorSignatureClick={onAuthorSignatureClick}
            hideFirstAssigneeName={mode === 'standalone' && requiredFirstStep?.locked === true}
          />
          {editable && approvalLines.length > 0 && (
            <p className="text-xs text-ink-400 mt-3">
              현장 기본 결재라인이 적용됩니다. 수정하려면 편집 버튼을 클릭하세요.
            </p>
          )}
          {resendApprovalInfo?.canResend && resendApprovalInfo.currentApprover && onResendApproval && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <span className="text-xs text-ink-500">
                현재 결재자: {resendApprovalInfo.currentApprover.signerName || resendApprovalInfo.currentApprover.member?.name}
                {resendApprovalInfo.currentApprover.signerTitle && (
                  <span className="ml-1">({resendApprovalInfo.currentApprover.signerTitle})</span>
                )}
              </span>
              <button
                type="button"
                onClick={onResendApproval}
                disabled={isResendingApproval}
                className="inline-flex items-center px-3 py-1.5 border border-line rounded-card-sm text-xs font-medium text-ink-700 bg-white hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                title="결재 링크를 재전송합니다 (SMS/이메일)"
              >
                {isResendingApproval ? (
                  <>
                    <svg className="animate-spin h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    전송 중...
                  </>
                ) : (
                  <>
                    <svg className="h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    결재 링크 재전송
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {modals}
    </Card>
  );
}
